import React, { createContext, useContext, useState, useEffect, ReactNode, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from './AuthContext';

export type ThemeMode = 'light' | 'dark' | 'system';

interface ThemeContextType {
  theme: ThemeMode;
  setTheme: (theme: ThemeMode) => Promise<void>;
  isLoading: boolean;
}

const ThemeContext = createContext<ThemeContextType | undefined>(undefined);

export const useTheme = () => {
  const context = useContext(ThemeContext);
  if (context === undefined) {
    throw new Error('useTheme must be used within a ThemeProvider');
  }
  return context;
};

interface ThemeProviderProps {
  children: ReactNode;
}

// Get the effective theme (resolves 'system' to actual light/dark based on OS preference)
const getEffectiveTheme = (theme: ThemeMode): 'light' | 'dark' => {
  if (theme === 'system') {
    return window.matchMedia('(prefers-color-scheme: dark)').matches ? 'dark' : 'light';
  }
  return theme;
};

// Apply dark class to document root
const applyTheme = (theme: ThemeMode) => {
  const effectiveTheme = getEffectiveTheme(theme);
  const root = document.documentElement;
  
  if (effectiveTheme === 'dark') {
    root.classList.add('dark');
  } else {
    root.classList.remove('dark');
  }
};

export const ThemeProvider: React.FC<ThemeProviderProps> = ({ children }) => {
  const { user } = useAuth();
  const [theme, setThemeState] = useState<ThemeMode>('system');
  const [isLoading, setIsLoading] = useState(true);

  // Fetch theme preference from API
  const fetchThemePreference = useCallback(async () => {
    if (!user) {
      // If not authenticated, default to system
      setThemeState('system');
      setIsLoading(false);
      applyTheme('system');
      return;
    }

    try {
      const response = await axios.get('/users/me/profile');
      const themePreference = response.data.theme_preference || 'system';
      setThemeState(themePreference as ThemeMode);
      applyTheme(themePreference as ThemeMode);
    } catch (error) {
      console.error('Error fetching theme preference:', error);
      // Default to system on error
      setThemeState('system');
      applyTheme('system');
    } finally {
      setIsLoading(false);
    }
  }, [user]);

  // Save theme preference to API
  const setTheme = useCallback(async (newTheme: ThemeMode) => {
    setThemeState(newTheme);
    applyTheme(newTheme);

    if (!user) {
      // If not authenticated, just update local state
      return;
    }

    try {
      await axios.patch('/users/me/profile', {
        theme_preference: newTheme
      });
    } catch (error) {
      console.error('Error saving theme preference:', error);
      // Revert on error
      fetchThemePreference();
    }
  }, [user, fetchThemePreference]);

  // Fetch theme preference when user changes
  useEffect(() => {
    fetchThemePreference();
  }, [fetchThemePreference]);

  // Listen to system preference changes when theme is 'system'
  useEffect(() => {
    if (theme !== 'system') {
      return;
    }

    const mediaQuery = window.matchMedia('(prefers-color-scheme: dark)');
    const handleChange = () => {
      applyTheme('system');
    };

    // Modern browsers
    if (mediaQuery.addEventListener) {
      mediaQuery.addEventListener('change', handleChange);
      return () => mediaQuery.removeEventListener('change', handleChange);
    } else {
      // Fallback for older browsers
      mediaQuery.addListener(handleChange);
      return () => mediaQuery.removeListener(handleChange);
    }
  }, [theme]);

  // Apply theme whenever it changes
  useEffect(() => {
    applyTheme(theme);
  }, [theme]);

  return (
    <ThemeContext.Provider value={{ theme, setTheme, isLoading }}>
      {children}
    </ThemeContext.Provider>
  );
};
